import type { SyncHasMany } from "@ember-data/model";
import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import type ModelRegistry from "@ember-data/types/registries/model";
import type CompoundSurvey from "parog-web/models/compound-survey";
import type DmpHealthCentreConfig from "parog-web/models/dmp-health-centre-config";
import type LearningSection from "parog-web/models/learning-section";
import type Patient from "parog-web/models/patient";
import type PrescriptionConfiguration from "parog-web/models/prescription-configuration";
import { sortBy } from "parog-web/utils/sort";
import type CareGiver from "./care-giver";
import type HealthCentreTag from "./health-centre-tag";

export default class HealthCentre extends Model {
  @attr("string") declare finess: string;

  @attr("string") declare code: string;
  @attr("string") declare name: string;
  @attr("string") declare productName: string;
  @attr("string")
  declare service: keyof (typeof HealthCentre)["serviceToTherapeuticalAreaId"];
  @attr("boolean") declare hasRestrictedMessaging: boolean;
  @attr("boolean") declare isgt: boolean;
  @attr("boolean") declare oncoToulouse: boolean;
  @attr("boolean") declare proIrc: boolean;
  @attr("boolean") declare healthCentrePatientSearch: boolean;
  @attr("boolean") declare isMrcBordeaux: boolean;
  @attr("string") declare uuid: string;
  @attr("boolean") declare isPartOfIdentificationDomain: boolean;
  /** A flag to activate the -pre-transplant folder feature for this health centre */
  @attr("boolean") declare preTransplant: boolean;
  /** A flag to activate the new patient-history dashboard */
  @attr("boolean") declare patientHistoryDashboardEnabled: boolean;
  /** A flag to activate for DigestWise patients if they are in the control group */
  @attr("boolean") declare controlGroupCentre: boolean;
  /** A flag to deactivate the posibility for patient to decalre some bio results */
  @attr("boolean") declare disableBioForPatient: boolean;
  /** A flag to deactivate the teleconsultation */
  @attr("boolean") declare teleconsultationDisabled: boolean;
  /** A flag to activate daily call to profiler */
  @attr("boolean") declare enableProfilerV2: boolean;
  /** A flag to activate the toucan dashboard */
  @attr("boolean") declare toucanEnabled: boolean;

  static serviceToTherapeuticalAreaId = {
    renal: "nephro",
    pulmonary: "pneumo",
    rheumatology: "rhumato",
    cancer: "onco",
    psychiatry: "mental",
    hepatology: "hepato",
    cardiology: "cardio",
    diabetology: "diabeto",
    gastroenterology: "digest",
    generic: "generic"
  };

  @belongsTo("dmp-health-centre-config", {
    async: false,
    inverse: "healthCentre"
  })
  declare dmpHealthCentreConfig: DmpHealthCentreConfig;

  @hasMany("health-centre-tag", {
    async: false,
    inverse: "healthCentres"
  })
  declare healthCentreTags?: HealthCentreTag[];

  @hasMany("patient", { async: false, inverse: "healthCentre" })
  declare patients: SyncHasMany<Patient>;

  @hasMany("learning-section", { async: false, inverse: "healthCentre" })
  declare learningSections: SyncHasMany<LearningSection>;

  @hasMany("compound-survey", {
    async: false,
    inverse: "healthCentres"
  })
  declare surveys: CompoundSurvey[];

  @belongsTo("prescription-configuration", {
    async: false,
    inverse: "healthCentre"
  })
  declare prescriptionConfiguration: PrescriptionConfiguration;

  @hasMany("care-giver", {
    async: false,
    inverse: "healthCentre"
  })
  declare careGivers: SyncHasMany<CareGiver>;

  get orderedLearningSections(): Array<LearningSection> {
    //@ts-expect-error TODO: update the learningSections type when @types/ember-data will be updated
    return this.learningSections.toSorted(sortBy("position"));
  }

  get shouldShowHlaContent(): boolean {
    const hlaUUIDHealthCentres = [
      "a9a0599c-2864-40d3-a98f-4d2137966386", // Hôpital Bicêtre AP-HP
      "f38415fb-c2a5-4ac2-a1a8-221be175c13e", // Necker Adultes
      "78024f08-8642-4584-a927-ec75f0bcae55", // APHP - La Pitié Salpêtrière
      "f0895652-6bf6-447a-b67f-805a7cc77f21", // CH RENE DUBOS - Pontoise
      "51f1ccc8-3417-4d09-9e32-47a151a48cde", // Hôpital Européen Georges-Pompidou
      "ce022ac5-3cb9-4e10-a520-a397741f0bf3" // Hôpital Foch
    ];
    return hlaUUIDHealthCentres.includes(this.uuid) && this.service === "renal";
  }

  // Try to find a TherapeuticalArea record from the `service` attribte,
  // either by mapping the service to its corresponding therapeuticalArea Id
  // or just with the service name
  get therapeuticalArea(): ModelRegistry["therapeutical-area"] | null {
    const areaId =
      HealthCentre.serviceToTherapeuticalAreaId[this.service] ?? this.service;
    return areaId ? this.store.peekRecord("therapeutical-area", areaId) : null;
  }

  get productNameCapitalized() {
    return this.productName.replace(
      /^(.*?)(wise)$/,
      (_match, p1, p2) =>
        p1.charAt(0).toUpperCase() +
        p1.slice(1) +
        p2.charAt(0).toUpperCase() +
        p2.slice(1)
    );
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "health-centre": HealthCentre;
  }
}
